import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import {
	ArrowRight,
	Clock,
	Lightbulb,
	MessageCircle,
	Pause,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { trackInterviewIntroViewed } from "@/lib/analytics-events";

export const Route = createFileRoute("/_app/_/onboarding/interview-intro")({
	component: InterviewIntroPage,
});

function InterviewIntroPage() {
	const navigate = useNavigate();

	useEffect(() => {
		trackInterviewIntroViewed();
	}, []);

	const tips = [
		{
			icon: Clock,
			title: "Take your time",
			description:
				"This conversation takes about 30 minutes. There's no rush.",
		},
		{
			icon: Lightbulb,
			title: "Be honest & detailed",
			description:
				"The more context you share, the better your strategy will be.",
		},
		{
			icon: Pause,
			title: "Pause anytime",
			description:
				"Need a break? The interview picks up where you left off.",
		},
	];

	return (
		<div className="h-dvh flex flex-col items-center justify-center px-6">
			<div className="w-full max-w-lg space-y-6">
				{/* Header */}
				<div className="text-center space-y-4">
					<div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10">
						<MessageCircle className="h-3.5 w-3.5 text-primary" />
						<span className="text-xs font-medium text-primary">
							AI Strategy Session
						</span>
					</div>
					<h1 className="text-3xl font-semibold text-foreground tracking-tight">
						Let's build your winning strategy
					</h1>
					<p className="text-muted-foreground">
						Xander, your AI campaign strategist, will ask you questions about
						your background, goals, and vision. This conversation shapes your
						personalized campaign plan.
					</p>
				</div>

				{/* Tips */}
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
					{tips.map((tip) => (
						<Card key={tip.title} className="border-border/50">
							<CardContent className="p-4">
								<div className="flex flex-col items-center text-center gap-3">
									<div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
										<tip.icon className="h-5 w-5 text-primary" />
									</div>
									<div className="space-y-1">
										<h3 className="font-medium text-foreground text-sm">
											{tip.title}
										</h3>
										<p className="text-xs text-muted-foreground">
											{tip.description}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					))}
				</div>

				{/* CTA */}
				<div className="text-center space-y-3">
					<Button
						size="lg"
						className="h-12 px-8"
						onClick={() => navigate({ to: "/onboarding/mic-setup" })}
					>
						Start Conversation
						<ArrowRight className="ml-2 h-4 w-4" />
					</Button>
					<p className="text-xs text-muted-foreground">
						You can pause and resume at any time
					</p>
				</div>
			</div>
		</div>
	);
}
